import * as React from 'react';
import styled, { DefaultTheme } from 'styled-components';
import breakpoints, { forDesktop, forIE } from '@westwing/ui-kit/breakPoints';
import WestwingNowLogo from '@westwing/ui-kit/Icons/WestwingNow';
import ArrowIcon from '@westwing/ui-kit/Icons/ArrowIcon';
import BurgerMenuIcon from '@westwing/ui-kit/Icons/BurgerIcon';
import IconsButtonTray from './IconsButtonTray';
import ShopTab from './ShopTab';
import ClubTab from './ClubTab';
import InfoSection from './InfoSection';
import Navigation from './Navigation';
import DynamicSearch from './DynamicSearch';
import useConfig from 'Client/hooks/useConfig';
import HybridLink from 'Client/components/Generic/Links/HybridLink';
import { HeaderBarBannerData } from 'Common/apiAdapters/types';
import { VictorQaClasses } from 'Common/qaIds';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import { HeaderModeType, useLayoutByPage } from 'Client/hooks/useLayoutByPage';
import useRouter from 'Client/hooks/useRouter';
import { ensureEndingSlash } from 'Client/urlService';
import paths from 'Common/routes/paths';
import { midWideScreenPadding, desktopPageWidthPadding } from 'Client/components/Generic/BoxHelpers';
import useBellaPush from 'Client/hooks/useBellaHistoryPush';

const OneHeaderWrapper = styled.div`
    position: relative;
    ${midWideScreenPadding}
    ${desktopPageWidthPadding}
`;

const OneHeaderLayout = styled.header<{ showMiniHeader: boolean }>`
    max-width: ${breakpoints.breakpointSemiWideScreens};
    margin: auto;
    display: grid;
    background: white;
    grid-template:
        'tabs   tabs         tabs    tabs    tabs    tabs' ${({ showMiniHeader }) => (showMiniHeader ? '0' : '46')}px
        'burger search       logo    logo    icons   icons ' ${({ theme }) => theme.oneHeaderModuleHeight}px
        / 35px 40px 1fr 1fr 40px 35px;

    ${forDesktop`
        height: 149px;
        max-width: ${breakpoints.breakpointWideScreens};
        /* safari 10.1 does not support minmax inside grid-template so this first grid-template rule is a fallback for safari 10.1 */
        grid-template:
            'tabs      tabs   tabs    tabs      .        info    info    '  46px
            'logo      logo   .       search    search   search  icons   '  103px
            /188px     41px   79px    68px      1fr      88px    302px   ;
        grid-template:
            'tabs      tabs    tabs    tabs     .        info                        info    '  46px
            'logo      logo    .       search   search   search                      icons   '  103px
            /188px     41px    79px    68px     1fr      minmax(88px, max-content)   302px   ;
    `}

    ${forIE`
        display: -ms-grid;
        -ms-grid-rows: 46px 103px;
        -ms-grid-columns: ${({ theme }: { theme: DefaultTheme }) =>
            theme.desktopPagePadding} 188px        41px     79px       68px        1fr      minmax(88px, max-content)   323px;
        background: transparent;
    `}
`;

const HeaderTabsWrapper = styled.div<{ showMiniHeader: boolean }>`
    grid-area: tabs;
    display: ${({ showMiniHeader }) => (showMiniHeader ? 'none' : 'flex')};
    border-top: 2px solid ${({ theme }) => theme.colors.turquoise100};
    ${forDesktop`
        border-top: none;
        display: flex;
    `}
    ${forIE`
        width: 376px;
        display: flex;
    `}
`;

const OutsideHeaderBackground = styled.div`
    display: none;
    ${forDesktop`
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 46px;
        display: block;
        background-color: ${({ theme }) => theme.colors.turquoise20};
    `}
`;

const BigLogo = styled(HybridLink)`
    display: block;
    grid-area: logo;
    ${forDesktop`
        background: ${({ theme }) => theme.colors.white};
        display: flex;
        align-items: center;
    `}

    ${forIE`
        -ms-grid-row: 2;
        -ms-grid-column: 2;
        -ms-grid-column-span: 2;
    `}
`;

const StyledWestwingNowLogo = styled(WestwingNowLogo)`
    width: 160px;
    margin: auto;
    height: 100%;
    display: block;

    ${forDesktop`
        width: 100%;
    `}
`;

const IconWrapper = styled.div<{ showMiniHeader: boolean }>`
    display: ${({ showMiniHeader }) => (showMiniHeader ? 'none' : 'block')};
    grid-area: burger;
    padding: 13px 0 0 13px;
    color: ${({ theme }) => theme.colors.charcoal100};
    width: 30px;
    ${forDesktop`
        display: none;
    `}
`;

const BackButtonIconWrapper = styled.div`
    grid-area: burger;
    padding: 20px 0 0 20px;
    color: ${({ theme }) => theme.colors.charcoal100};
    width: 30px;
    ${forDesktop`
        display: none;
    `}
`;

const StyledHeaderBackground = styled.div`
    grid-column: 1/-1;
    grid-row: 2/3;
    background: ${({ theme }) => theme.colors.white};
    ${forDesktop`
        display: block;
    `}

    ${forIE`
        display: none;
    `}
`;
const StyledHeaderTabsBackground = styled.div`
    display: none;
    grid-column: 1/-1;
    grid-row: 1/2;
    background: ${({ theme }) => theme.colors.turquoise100};
    ${forDesktop`
        display: block;
    `}

    ${forIE`
        display: none;
    `}
`;

interface Props {
    handleBurgerMenuIconClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    handleLogoClick: (event: React.MouseEvent<HTMLElement, MouseEvent>) => void;
    wishListCount: number;
    minOrderValue: number | null;
    headerBarBanner?: HeaderBarBannerData;
}

const OneHeader = ({ handleBurgerMenuIconClick, handleLogoClick, minOrderValue, wishListCount }: Props) => {
    const {
        links: { home: HomeLink = '', myWishlist, cart, myAccount },
    } = useConfig();

    const { shopBellaCart = false } = useFeatureToggle();
    const { headerMode } = useLayoutByPage();
    const showMiniHeader = headerMode === HeaderModeType.MINI;
    const { history } = useRouter();
    const bellaPush = useBellaPush();
    const cartUrl = shopBellaCart ? ensureEndingSlash(paths.CART) : cart;

    const handleBackButtonClick = () => {
        /**
         * We need to do this hack because of following requirement:
         * 1. User is on cart page
         * 2. User goes to checkout page
         * 3. User comes back to cart page
         * 4. User clicks back button, user should not goto checkout page
         *    They should goto homepage
         */
        const isFromCheckout = document.referrer.includes('checkout/');
        // Check if history state is null to make sure it came from alice page
        // we do not change referrer with client side navigation.
        if (isFromCheckout && window.history.state === null) {
            bellaPush('/');
        } else {
            history.goBack();
        }
    };

    return (
        <div>
            <OneHeaderWrapper>
                <OneHeaderLayout showMiniHeader={showMiniHeader}>
                    <OutsideHeaderBackground />
                    <StyledHeaderTabsBackground />
                    <HeaderTabsWrapper data-testid="header-tabs-wrapper" showMiniHeader={showMiniHeader}>
                        <ClubTab />
                        <ShopTab />
                    </HeaderTabsWrapper>
                    <StyledHeaderBackground />
                    {showMiniHeader && (
                        <BackButtonIconWrapper data-testid="header-back-button" onClick={handleBackButtonClick}>
                            <ArrowIcon size={13} />
                        </BackButtonIconWrapper>
                    )}
                    <IconWrapper
                        showMiniHeader={showMiniHeader}
                        className={VictorQaClasses.headerBurgerMenuIcon}
                        onClick={handleBurgerMenuIconClick}
                        data-testid="burger_menu_icon"
                    >
                        <BurgerMenuIcon />
                    </IconWrapper>
                    <DynamicSearch />
                    <IconsButtonTray
                        wishListItemCount={wishListCount}
                        wishListUrl={myWishlist}
                        cartUrl={cartUrl}
                        myAccountUrl={myAccount}
                    />
                    <BigLogo to={HomeLink} onClick={handleLogoClick}>
                        <StyledWestwingNowLogo />
                    </BigLogo>
                    <InfoSection minOrderValue={minOrderValue} />
                </OneHeaderLayout>
            </OneHeaderWrapper>
            <Navigation />
        </div>
    );
};

export default OneHeader;
